/**
 * @fileoverview The invitation a plant with no photographs shows instead of a picture.
 *
 * Full size again, the same 4:3 as a photograph, so a garden of cards keeps
 * one shape whether a plant has been photographed or not, and the whole
 * frame is the button. What stays gone is what made the old one tall
 * without cause: the sentence, the second camera in an empty strip below.
 *
 * A card used to spend 316px saying this — a 4:3 grey frame holding an icon,
 * the words "No photos yet", a sentence explaining what photographs are for,
 * and a button; and then, directly beneath it, a second camera button sitting
 * alone in an empty thumbnail strip. Two stacked invitations to do the same
 * thing, 41% of the card, above a plant whose name had not appeared yet. On a
 * phone that was most of a screen of furniture per unphotographed plant.
 *
 * One target says it once. The explanation is gone with the space: a camera
 * and "Add a photo" is not a sentence anybody needed help with.
 *
 * Where: the plant card (PlantCard) and the bed card (LocationCard), in the
 * picture's place while there is no photo.
 * API: none; the caller opens the upload dialog.
 * Words (en/es): plants.card.addFirstPhoto, plants.card.addFirstPhotoFor.
 *
 * @module AddFirstPhoto
 */

import React from 'react';
import { Camera } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** What the invitation opens, and the plant it names. */
interface AddFirstPhotoProps {
  /** Opens the upload dialog. */
  onTakePhoto: () => void;
  /** Named in the label read to screen readers, so it says which plant. */
  varietyName: string;
}

/**
 * The picture's place on a card that has none yet: one large target to take the first.
 *
 * @param props - AddFirstPhotoProps.
 */
export const AddFirstPhoto: React.FC<AddFirstPhotoProps> = ({ onTakePhoto, varietyName }) => {
  const { reactTranslate: t } = useTranslation();

  return (
    <div className="relative aspect-[4/3] w-full border-b bg-muted/40">
      <button
        type="button"
        onClick={onTakePhoto}
        aria-label={t('plants.card.addFirstPhotoFor', { plant: varietyName })}
        className="flex h-full w-full flex-col items-center justify-center gap-3 transition-colors hover:bg-muted"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full border bg-background shadow-sm">
          <Camera className="h-7 w-7 text-foreground" aria-hidden="true" />
        </span>
        <span className="text-base font-medium">{t('plants.card.addFirstPhoto')}</span>
      </button>
    </div>
  );
};
